import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import axios from "axios";
import {
  Package, Search, BellRing, Plus, StickyNote, Pencil, Check, Trash2, X, Truck, ClipboardPaste, Loader2,
} from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "@/components/ui/sonner";
import {
  bomSummary, etaTone, etaSubLabel, fmtBomDate, fmtMoney, todayIso, MTC_META, MTC_NEXT,
} from "./bomUtils";
import { Avatar } from "./BomCreator";

const API = process.env.REACT_APP_BACKEND_URL;

const ETA_CHIP = {
  none: "border border-dashed border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))]",
  ok:   "text-[hsl(var(--foreground))] px-0",
  soon: "bg-amber-500/12 text-amber-700 dark:text-amber-400",
  late: "bg-red-500/10 text-red-700 dark:text-red-400",
  recd: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
};
const ETA_REASONS = ["Supplier delay", "Customs / clearance", "Transport", "Earlier than planned", "Other"];

const inputCls = "w-full h-8 px-2 rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--background))] text-[12.5px] focus:outline-none focus:ring-2 focus:ring-orange-500/40";
const btnPrimary = "h-8 px-3.5 rounded-lg text-[12px] font-semibold text-white bg-orange-500 hover:bg-orange-600 disabled:opacity-45 disabled:cursor-not-allowed inline-flex items-center gap-1.5";
const btnGhost = "h-8 px-3 rounded-lg text-[12px] font-semibold text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))]/60";

/* The Child Part Tracker — a Notion-style ledger of one assembly's parts.
 * Who edits what (mirrors the backend):
 *   canDefine (PO Received or Material) — add parts; edit part no / description
 *     / qty while a part is still Not ordered; remove a Not-ordered part.
 *   canBuy (Material) — ETA / receive, supplier, landed cost (₹/$), MTC.
 *   everyone — notes. Cost columns only render when canViewCost. */
export default function ChildPartTracker({ open, item, currentUser, perms, onClose, onItemUpdate, onPasteMore }) {
  const { canDefine, canBuy, canViewCost } = perms || {};
  const [query, setQuery] = useState("");
  const [pop, setPop] = useState(null);           // { kind: "eta"|"cost"|"watch", id }
  const [noteFor, setNoteFor] = useState(null);
  const [adding, setAdding] = useState(false);
  const [people, setPeople] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setQuery(""); setPop(null); setNoteFor(null); setAdding(false);
    axios.get(`${API}/api/users/mentionables`).then(r => setPeople(r.data || [])).catch(() => {});
  }, [open]);

  const call = useCallback(async (fn, okMsg) => {
    setBusy(true);
    try {
      const res = await fn();
      onItemUpdate?.(res.data);
      if (okMsg) toast.success(okMsg);
      return res.data;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save that change");
      return null;
    } finally {
      setBusy(false);
    }
  }, [onItemUpdate]);

  const patchPart = useCallback((pid, body, okMsg) =>
    call(() => axios.patch(`${API}/api/line-items/${item.id}/bom/parts/${pid}`, body), okMsg), [call, item]);

  const bom = useMemo(() => item?.bom || [], [item]);
  const s = bomSummary(bom);
  const q = query.trim().toLowerCase();
  const rows = useMemo(() => bom.filter(p => !q || `${p.part_no} ${p.description} ${p.supplier}`.toLowerCase().includes(q)), [bom, q]);

  const nameOf = (id) => people.find(p => p.id === id)?.name;
  const alertNames = [item?.bom_owner_name, ...((item?.bom_watchers || []).map(nameOf))].filter(Boolean);
  const myId = currentUser?.user_id || currentUser?.id;

  const totals = useMemo(() => {
    const inr = bom.filter(p => p.cost_currency !== "USD").reduce((a, p) => a + (p.cost || 0) * (p.qty || 0), 0);
    const usd = bom.filter(p => p.cost_currency === "USD").reduce((a, p) => a + (p.cost || 0) * (p.qty || 0), 0);
    const spend = (item?.spend_entries || []).filter(e => e.source === "bom" && !e.deleted_at).reduce((a, e) => a + (e.amount || 0), 0);
    return { inr, usd, spend };
  }, [bom, item]);

  if (!open || !item) return null;
  const pct = (k) => (s.n ? `${(100 * k) / s.n}%` : "0%");
  const cols = 7 + (canViewCost ? 1 : 0);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))] w-[97vw] xl:w-[1240px] max-w-[97vw] p-0 overflow-hidden flex flex-col max-h-[90vh] gap-0" data-testid="child-part-tracker">
        {/* header band */}
        <div className="bg-blue-500/[0.07] dark:bg-blue-400/[0.08] px-5 py-3.5 pr-12 flex items-center gap-3 flex-wrap shrink-0">
          <DialogTitle className="flex items-center gap-2.5 text-blue-900 dark:text-blue-200 font-bold text-[15px] uppercase tracking-[0.02em]">
            <Package className="w-5 h-5" /> Material — Child Part Tracker
          </DialogTitle>
          <DialogDescription className="text-[12px] text-blue-900/75 dark:text-blue-200/75 m-0 truncate max-w-[420px]">
            {item.po_number} L{item.line_number} · {item.item_description} · Qty {item.quantity}
          </DialogDescription>
          <span className="flex-1" />
          <Popover open={pop?.kind === "watch"} onOpenChange={(o) => setPop(o ? { kind: "watch" } : null)}>
            <PopoverTrigger asChild>
              <button type="button" className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-lg text-[12px] font-semibold text-blue-900 dark:text-blue-200 bg-[hsl(var(--card))]/70 border border-blue-900/15 dark:border-blue-200/20 hover:bg-[hsl(var(--card))]" data-testid="bom-alerts-btn">
                <BellRing className="w-3.5 h-3.5" /> ETA alerts · {alertNames.length || 1} {alertNames.length === 1 ? "person" : "people"}
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-[320px] p-3.5">
              <WatchersEditor item={item} people={people} myId={myId} canEdit={canDefine || myId === item.bom_owner_id}
                onSave={(ids) => call(() => axios.put(`${API}/api/line-items/${item.id}/bom/watchers`, { user_ids: ids }), "Alert list updated")} />
            </PopoverContent>
          </Popover>
          <span className="text-[12.5px] text-blue-900/80 dark:text-blue-200/80 font-mono-data">{s.n} child parts</span>
        </div>

        {/* summary + search */}
        <div className="flex items-center gap-3.5 px-5 py-2.5 border-b border-[hsl(var(--border))] flex-wrap text-[12px] text-[hsl(var(--muted-foreground))] shrink-0">
          <div className="h-1.5 w-[180px] max-w-full rounded-full bg-[hsl(var(--muted))] flex overflow-hidden" aria-hidden="true">
            <i className="block h-full bg-emerald-500" style={{ width: pct(s.received) }} />
            <i className="block h-full bg-red-500" style={{ width: pct(s.late) }} />
            <i className="block h-full bg-blue-500" style={{ width: pct(s.ordered - s.late) }} />
          </div>
          <Count dot="bg-emerald-500" n={s.received} label="received" />
          {s.late > 0 && <Count dot="bg-red-500" n={s.late} label="late" />}
          <Count dot="bg-blue-500" n={s.ordered - s.late} label="on order" />
          {s.notOrdered > 0 && <Count dot="border-[1.5px] border-dashed border-slate-400" n={s.notOrdered} label="not ordered" />}
          {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
          <label className="ml-auto flex items-center gap-1.5 h-8 px-2.5 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))]">
            <Search className="w-3.5 h-3.5" />
            <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search parts or suppliers" aria-label="Search parts"
              className="bg-transparent outline-none text-[12.5px] w-[170px] text-[hsl(var(--foreground))]" />
          </label>
        </div>

        {/* table */}
        <div className="flex-1 overflow-auto px-4 pb-4">
          {/* table-fixed + explicit widths: columns never resize on hover,
              edit or popover — only the description column flexes. */}
          <table className="w-full min-w-[1080px] table-fixed mt-3.5 border-separate border-spacing-0 rounded-xl border border-[hsl(var(--border))] overflow-hidden text-[13px]">
            <colgroup>
              <col className="w-[136px]" />
              <col />
              <col className="w-[78px]" />
              <col className="w-[150px]" />
              <col className="w-[160px]" />
              {canViewCost && <col className="w-[150px]" />}
              <col className="w-[132px]" />
              <col className="w-[40px]" />
            </colgroup>
            <thead>
              <tr className="bg-[hsl(var(--muted))]/55 text-[12px] font-semibold text-[hsl(var(--foreground))]">
                <Th>Child Part No</Th>
                <Th>Child Part Description</Th>
                <Th right>Qty</Th>
                <Th>RM ETA</Th>
                <Th>Supplier</Th>
                {canViewCost && <Th>Landed cost / unit</Th>}
                <Th>MTC Approval</Th>
                <Th last></Th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && bom.length > 0 && (
                <tr><td colSpan={cols} className="px-4 py-6 text-center text-[12.5px] text-[hsl(var(--muted-foreground))]">No parts match "{query}".</td></tr>
              )}
              {rows.map(p => {
                const pending = p.status === "Pending";
                const editDef = canDefine && pending;
                return (
                  <tr key={p.id} className="group align-top hover:bg-[hsl(var(--muted))]/25" data-testid={`bom-row-${p.id}`}>
                    <Td>
                      <EditableText value={p.part_no} placeholder="—" mono disabled={!editDef}
                        lockedTitle={canDefine ? "Locked — the part is ordered" : undefined}
                        onSave={(v) => patchPart(p.id, { part_no: v })} />
                    </Td>
                    <Td className="min-w-[300px]">
                      {/* Notes live here now: a tiny floating "+ note" in the
                          cell's top-right corner, in a gutter the text always
                          reserves (gutter="wide") — so it never shifts layout. */}
                      <div className="relative">
                      <EditableText value={p.description} disabled={!editDef} required multiline gutter="wide"
                        lockedTitle={canDefine ? "Locked — the part is ordered" : undefined}
                        onSave={(v) => patchPart(p.id, { description: v })} />
                      <button type="button" onClick={() => setNoteFor(noteFor === p.id ? null : p.id)}
                        className={`absolute -top-0.5 -right-1 inline-flex items-center gap-px h-[22px] px-1 rounded-md text-[hsl(var(--muted-foreground))] border border-transparent transition-opacity hover:opacity-100 hover:border-[hsl(var(--border))] hover:bg-[hsl(var(--card))] hover:text-[hsl(var(--foreground))] focus-visible:opacity-100 ${noteFor === p.id ? "opacity-100 text-orange-600" : "opacity-30 group-hover:opacity-80"}`}
                        title="Add a note" aria-label="Add a note" data-testid={`bom-note-btn-${p.id}`}>
                        <Plus className="w-2.5 h-2.5" strokeWidth={3} /><StickyNote className="w-3 h-3" />
                      </button>
                      </div>
                      {(p.notes || []).map(n => (
                        <div key={n.id} className="mt-1 text-[11.5px] leading-snug text-[hsl(var(--muted-foreground))] flex gap-1.5">
                          <b className="text-[9.5px] tracking-widest pt-px shrink-0 opacity-80">NOTES:</b>
                          <span>{n.text} <span className="opacity-70">· {n.by}</span></span>
                        </div>
                      ))}
                      {noteFor === p.id && (
                        <NoteInput onCancel={() => setNoteFor(null)}
                          onSave={async (t) => { const ok = await call(() => axios.post(`${API}/api/line-items/${item.id}/bom/parts/${p.id}/notes`, { text: t })); if (ok) setNoteFor(null); }} />
                      )}
                    </Td>
                    <Td right>
                      <EditableText value={String(p.qty ?? 1)} mono numeric disabled={!editDef} right
                        lockedTitle={canDefine ? "Locked — the part is ordered" : undefined}
                        onSave={(v) => patchPart(p.id, { qty: Number(v) })} />
                    </Td>
                    <Td>
                      <Popover open={pop?.kind === "eta" && pop.id === p.id} onOpenChange={(o) => setPop(o ? { kind: "eta", id: p.id } : null)}>
                        <PopoverTrigger asChild>
                          <button type="button" disabled={!canBuy}
                            className="text-left rounded-md -mx-1.5 -my-1 px-1.5 py-1 enabled:hover:bg-[hsl(var(--muted))]/70 disabled:cursor-default"
                            title={canBuy ? "Revise ETA or mark received" : "Material updates this"} data-testid={`bom-eta-${p.id}`}>
                            <EtaCell p={p} />
                          </button>
                        </PopoverTrigger>
                        <PopoverContent align="start" className="w-[310px] p-3.5">
                          <EtaMenu p={p} alertNames={alertNames.filter(n => n !== currentUser?.name)}
                            onDone={() => setPop(null)} patch={(body, msg) => patchPart(p.id, body, msg)} />
                        </PopoverContent>
                      </Popover>
                    </Td>
                    <Td>
                      <EditableText value={p.supplier || ""} placeholder={canBuy ? "Add supplier" : "—"} disabled={!canBuy}
                        onSave={(v) => patchPart(p.id, { supplier: v })} />
                    </Td>
                    {canViewCost && (
                      <Td>
                        <Popover open={pop?.kind === "cost" && pop.id === p.id} onOpenChange={(o) => setPop(o ? { kind: "cost", id: p.id } : null)}>
                          <PopoverTrigger asChild>
                            <button type="button" disabled={!canBuy}
                              className="group/c text-left rounded-md -mx-1.5 -my-1 px-1.5 py-1 enabled:hover:bg-[hsl(var(--muted))]/70 disabled:cursor-default inline-flex flex-col"
                              data-testid={`bom-cost-${p.id}`}>
                              {p.cost ? (
                                <>
                                  <span className="font-mono-data font-semibold whitespace-nowrap inline-flex items-center gap-1">
                                    {fmtMoney(p.cost, p.cost_currency)}
                                    <span className="text-[10px] font-bold text-[hsl(var(--muted-foreground))]">{p.cost_currency === "USD" ? "USD" : "INR"}</span>
                                    {canBuy && <Pencil className="w-3 h-3 opacity-0 group-hover/c:opacity-50 transition-opacity" />}
                                  </span>
                                  {p.cost_currency !== "USD" && p.fx_inr_usd > 0 && (
                                    <span className="text-[10.5px] text-[hsl(var(--muted-foreground))] font-mono-data">≈ ${(p.cost * p.fx_inr_usd).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                  )}
                                </>
                              ) : (
                                <span className="text-[hsl(var(--muted-foreground))] text-[12.5px]">{canBuy ? "Add cost" : "—"}</span>
                              )}
                            </button>
                          </PopoverTrigger>
                          <PopoverContent align="start" className="w-[300px] p-3.5">
                            <CostEditor p={p} onDone={() => setPop(null)} patch={(body) => patchPart(p.id, body)} />
                          </PopoverContent>
                        </Popover>
                      </Td>
                    )}
                    <Td>
                      {(() => {
                        const m = MTC_META[p.mtc] || MTC_META.pending;
                        return (
                          <button type="button" disabled={!canBuy}
                            onClick={() => patchPart(p.id, { mtc: MTC_NEXT[p.mtc] || "approved" })}
                            className={`inline-flex items-center h-6 px-2.5 rounded-full border text-[11.5px] font-semibold whitespace-nowrap ${m.cls} enabled:hover:brightness-95 disabled:cursor-default`}
                            title={canBuy ? "Click to change: Not approved → Approved → Not required" : "Material updates this"}
                            data-testid={`bom-mtc-${p.id}`}>
                            {m.label}
                          </button>
                        );
                      })()}
                    </Td>
                    <Td last>
                      {editDef && (
                        <button type="button" title="Remove this part (only while not ordered)" aria-label="Remove part"
                          onClick={() => call(() => axios.delete(`${API}/api/line-items/${item.id}/bom/parts/${p.id}`), "Part removed")}
                          className="w-7 h-7 inline-flex items-center justify-center rounded-md text-[hsl(var(--muted-foreground))] opacity-0 group-hover:opacity-100 focus:opacity-100 hover:text-red-600 hover:bg-red-500/10 transition-opacity">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </Td>
                  </tr>
                );
              })}

              {canDefine && (adding ? (
                <NewPartRow cols={cols} onCancel={() => setAdding(false)}
                  onAdd={(part) => call(() => axios.post(`${API}/api/line-items/${item.id}/bom/parts`, { parts: [part] }))} />
              ) : (
                <tr>
                  <td colSpan={cols} className="px-3.5 py-2.5 border-t border-[hsl(var(--border))]/60 bg-[repeating-linear-gradient(135deg,transparent_0_8px,hsl(var(--muted)/0.45)_8px_9px)]">
                    <div className="flex items-center gap-3">
                      <button type="button" onClick={() => setAdding(true)}
                        className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-[hsl(var(--muted-foreground))] hover:text-orange-600" data-testid="bom-ghost-add">
                        <Plus className="w-4 h-4" /> Add child part
                      </button>
                      <button type="button" onClick={() => onPasteMore?.(item)}
                        className="inline-flex items-center gap-1 text-[11.5px] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]">
                        <ClipboardPaste className="w-3.5 h-3.5" /> or paste several
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
            {canViewCost && (
              <tfoot>
                <tr className="bg-[hsl(var(--muted))]/55 text-[12px] font-semibold">
                  <td colSpan={4} className="px-3.5 py-2.5">{s.n} child parts · total landed cost (cost × qty)</td>
                  <td colSpan={cols - 4} className="px-3.5 py-2.5 text-right whitespace-nowrap">
                    <span className="font-mono-data">{fmtMoney(totals.inr, "INR")}</span>
                    <span className="text-[hsl(var(--muted-foreground))] font-normal"> + </span>
                    <span className="font-mono-data">{fmtMoney(totals.usd, "USD")}</span>
                    <span className="text-[hsl(var(--muted-foreground))] font-normal"> · recorded as </span>
                    <span className="font-mono-data">{fmtMoney(totals.spend, "USD")}</span>
                    <span className="text-[hsl(var(--muted-foreground))] font-normal"> Material spend</span>
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
          {!canBuy && !canDefine && (
            <p className="mt-2.5 text-[11.5px] text-[hsl(var(--muted-foreground))]">View only — Material updates ETAs, suppliers, costs and MTC. Anyone can add a note.</p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ── small pieces ───────────────────────────────────────────────────────── */
function Th({ children, right, center, last }) {
  return (
    <th className={`px-3.5 py-2.5 font-semibold whitespace-nowrap border-b border-[hsl(var(--border))] ${last ? "" : "border-r border-r-[hsl(var(--border))]/60"} ${right ? "text-right" : center ? "text-center" : "text-left"}`}>
      {children}
    </th>
  );
}
function Td({ children, right, center, last, className = "" }) {
  return (
    <td className={`px-3.5 py-3 border-t border-[hsl(var(--border))]/60 ${last ? "w-9 px-1" : "border-r border-r-[hsl(var(--border))]/50"} ${right ? "text-right" : center ? "text-center" : ""} ${className}`}>
      {children}
    </td>
  );
}
function Count({ dot, n, label }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`w-2 h-2 rounded-full inline-block ${dot}`} />
      <b className="font-mono-data text-[hsl(var(--foreground))]">{n}</b> {label}
    </span>
  );
}

function EtaCell({ p }) {
  const tone = etaTone(p);
  const label = p.status === "Received" ? `✓ ${fmtBomDate(p.received_at || p.eta)}`
    : tone === "none" ? "Not ordered" : fmtBomDate(p.eta);
  const sub = etaSubLabel(p);
  const hist = p.status !== "Received" ? (p.eta_history || []) : [];
  return (
    <span className="inline-flex flex-col items-start">
      <span className={`inline-flex items-center rounded-md px-2 py-[3px] font-mono-data font-semibold text-[12px] whitespace-nowrap ${ETA_CHIP[tone]}`}>{label}</span>
      {(sub || hist.length > 0) && (
        <span className="text-[10.5px] text-[hsl(var(--muted-foreground))] mt-0.5 whitespace-nowrap">
          {sub}{sub && hist.length ? " · " : ""}
          {hist.length > 0 && <><s>{fmtBomDate(hist[hist.length - 1].eta)}</s> · revised {hist.length}×</>}
        </span>
      )}
    </span>
  );
}

function EtaMenu({ p, alertNames, onDone, patch }) {
  const [eta, setEta] = useState(p.eta || "");
  const [why, setWhy] = useState(ETA_REASONS[0]);
  const [supplier, setSupplier] = useState(p.supplier || "");
  const [recd, setRecd] = useState(todayIso());
  const go = async (body, msg) => { const ok = await patch(body, msg); if (ok) onDone(); };

  if (p.status === "Received") {
    return (
      <div className="flex flex-col gap-2.5">
        <h4 className="font-semibold text-[14px]">{p.part_no || "Part"} · received {fmtBomDate(p.received_at)}</h4>
        <button type="button" className="text-left rounded-lg px-2.5 py-2 hover:bg-[hsl(var(--muted))]/70 text-[13px]"
          onClick={() => go({ status: "Ordered" }, "Back to on order")}>
          Undo received<span className="block text-[11px] text-[hsl(var(--muted-foreground))]">Back to on order, ETA {fmtBomDate(p.eta) || "—"}</span>
        </button>
      </div>
    );
  }
  if (p.status === "Pending") {
    return (
      <div className="flex flex-col gap-2.5">
        <h4 className="font-semibold text-[14px]">Mark {p.part_no || "this part"} as ordered</h4>
        <label className="text-[11px] font-semibold text-[hsl(var(--muted-foreground))] flex flex-col gap-1">Supplier
          <input className={inputCls} value={supplier} onChange={e => setSupplier(e.target.value)} placeholder="Who is it ordered from?" autoFocus />
        </label>
        <label className="text-[11px] font-semibold text-[hsl(var(--muted-foreground))] flex flex-col gap-1">ETA
          <input type="date" className={inputCls} value={eta} onChange={e => setEta(e.target.value)} />
        </label>
        <div className="flex justify-end gap-1.5">
          <button type="button" className={btnGhost} onClick={onDone}>Cancel</button>
          <button type="button" className={btnPrimary} disabled={!eta}
            title={eta ? "" : "Pick the ETA first"}
            onClick={() => go({ status: "Ordered", eta, supplier: supplier.trim() }, "Marked ordered")}>Mark ordered</button>
        </div>
      </div>
    );
  }
  const changed = eta && eta !== p.eta;
  return (
    <div className="flex flex-col gap-2.5">
      <h4 className="font-semibold text-[14px]">{p.part_no || "Part"} · ETA {fmtBomDate(p.eta)}</h4>
      <div className="flex items-end gap-2">
        <label className="flex-1 text-[11px] font-semibold text-[hsl(var(--muted-foreground))] flex flex-col gap-1">Received on
          <input type="date" className={inputCls} value={recd} max={todayIso()} onChange={e => setRecd(e.target.value)} />
        </label>
        <button type="button" className="h-8 px-3 rounded-lg text-[12px] font-semibold text-white bg-emerald-600 hover:bg-emerald-700 inline-flex items-center gap-1"
          onClick={() => go({ status: "Received", received_at: recd }, "Marked received")} data-testid="bom-mark-received">
          <Check className="w-3.5 h-3.5" /> Mark received
        </button>
      </div>
      <div className="border-t border-[hsl(var(--border))] pt-2.5 flex flex-col gap-2">
        <label className="text-[11px] font-semibold text-[hsl(var(--muted-foreground))] flex flex-col gap-1">Revise ETA to
          <input type="date" className={inputCls} value={eta} onChange={e => setEta(e.target.value)} />
        </label>
        <label className="text-[11px] font-semibold text-[hsl(var(--muted-foreground))] flex flex-col gap-1">Why? (goes in the alert)
          <select className={inputCls} value={why} onChange={e => setWhy(e.target.value)}>
            {ETA_REASONS.map(r => <option key={r}>{r}</option>)}
          </select>
        </label>
        <div className="flex gap-1.5 text-[11.5px] text-[hsl(var(--muted-foreground))]">
          <BellRing className="w-3.5 h-3.5 mt-px shrink-0" />
          {alertNames.length
            ? <span>Will alert <b className="text-[hsl(var(--foreground))]">{alertNames.join(", ")}</b></span>
            : <span>No one else is on the alert list.</span>}
        </div>
        <div className="flex justify-between gap-1.5">
          <button type="button" className={btnGhost} onClick={() => go({ status: "Pending" }, "Back to not ordered")}
            title="Undo the order — clears the ETA from the plan">Not ordered</button>
          <div className="flex gap-1.5">
            <button type="button" className={btnGhost} onClick={onDone}>Cancel</button>
            <button type="button" className={btnPrimary} disabled={!changed}
              onClick={() => go({ eta, eta_reason: why }, "ETA revised")}>Revise &amp; alert</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function CostEditor({ p, onDone, patch }) {
  const [amt, setAmt] = useState(p.cost ? String(p.cost) : "");
  const [cur, setCur] = useState(p.cost_currency === "USD" ? "USD" : "INR");
  const valid = amt === "" || (/^\d+(\.\d+)?$/.test(amt.trim()));
  const save = async () => { if (!valid) return; const ok = await patch({ cost: Number(amt || 0), cost_currency: cur }); if (ok) onDone(); };
  return (
    <div className="flex flex-col gap-2.5">
      <h4 className="font-semibold text-[14px]">Landed cost per unit · {p.part_no || "part"}</h4>
      <div className="flex gap-2 items-center">
        <div className="relative flex-1">
          <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[14px] font-bold text-[hsl(var(--muted-foreground))]">{cur === "USD" ? "$" : "₹"}</span>
          <input autoFocus inputMode="decimal" value={amt} onChange={e => setAmt(e.target.value)} onKeyDown={e => e.key === "Enter" && save()}
            className={`${inputCls} pl-7 font-mono-data font-bold text-[15px] h-9 ${valid ? "" : "border-red-500"}`} placeholder="0" aria-label="Landed cost per unit" />
        </div>
        <div role="radiogroup" aria-label="Currency" className="inline-flex p-[2px] rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/50">
          {[["INR", "₹ INR"], ["USD", "$ USD"]].map(([k, l]) => (
            <button key={k} type="button" role="radio" aria-checked={cur === k} onClick={() => setCur(k)}
              className={`h-7 px-2.5 rounded-full text-[11.5px] font-semibold ${cur === k ? "bg-[hsl(var(--card))] shadow-[0_0_0_1px_hsl(var(--border))]" : "text-[hsl(var(--muted-foreground))]"}`}>{l}</button>
          ))}
        </div>
      </div>
      <div className="flex gap-2 rounded-lg bg-[hsl(var(--muted))]/60 px-2.5 py-2 text-[11.5px] text-[hsl(var(--muted-foreground))] leading-snug">
        <Truck className="w-3.5 h-3.5 mt-px shrink-0" />
        <span>Enter the <b className="text-[hsl(var(--foreground))]">landed</b> cost per unit — <b className="text-[hsl(var(--foreground))]">including customs duty and transport</b>, not just the supplier's price. Rupee costs are recorded in Material spend in dollars at today's rate.</span>
      </div>
      <div className="flex justify-end gap-1.5">
        <button type="button" className={btnGhost} onClick={onDone}>Cancel</button>
        <button type="button" className={btnPrimary} disabled={!valid} onClick={save}>Save cost</button>
      </div>
    </div>
  );
}

function WatchersEditor({ item, people, myId, canEdit, onSave }) {
  const [ids, setIds] = useState(item.bom_watchers || []);
  useEffect(() => setIds(item.bom_watchers || []), [item.bom_watchers]);
  const nameOf = (id) => people.find(p => p.id === id)?.name || "User";
  const dirty = JSON.stringify(ids) !== JSON.stringify(item.bom_watchers || []);
  const pickable = people.filter(p => p.id !== item.bom_owner_id && !ids.includes(p.id));
  return (
    <div className="flex flex-col gap-2.5">
      <h4 className="font-semibold text-[14px]">ETA alerts for this assembly</h4>
      <p className="text-[11.5px] text-[hsl(var(--muted-foreground))] -mt-1">Bell notification and email whenever a child part's ETA is revised.</p>
      <div className="flex flex-wrap gap-1.5">
        {item.bom_owner_name && (
          <span className="inline-flex items-center gap-1.5 h-7 pl-1 pr-3 rounded-full bg-[hsl(var(--muted))]/60 border border-[hsl(var(--border))] text-[12px] font-semibold">
            <Avatar name={item.bom_owner_name} /> {item.bom_owner_name} · made the list
          </span>
        )}
        {ids.map(id => (
          <span key={id} className="inline-flex items-center gap-1.5 h-7 pl-1 pr-1 rounded-full bg-[hsl(var(--muted))]/60 border border-[hsl(var(--border))] text-[12px] font-semibold">
            <Avatar name={nameOf(id)} /> {nameOf(id)}{id === myId ? " · you" : ""}
            {canEdit && (
              <button type="button" aria-label={`Remove ${nameOf(id)}`} onClick={() => setIds(v => v.filter(x => x !== id))}
                className="w-5 h-5 inline-flex items-center justify-center rounded-full text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))]"><X className="w-3 h-3" /></button>
            )}
          </span>
        ))}
      </div>
      {canEdit ? (
        <>
          <select value="" onChange={e => e.target.value && setIds(v => [...v, e.target.value])} className={inputCls} aria-label="Add a person">
            <option value="">+ Add person…</option>
            {pickable.map(p => <option key={p.id} value={p.id}>{p.name || p.email}{p.role ? ` · ${p.role}` : ""}</option>)}
          </select>
          <div className="flex justify-end">
            <button type="button" className={btnPrimary} disabled={!dirty} onClick={() => onSave(ids)}>Save</button>
          </div>
        </>
      ) : (
        <p className="text-[11.5px] text-[hsl(var(--muted-foreground))]">Only the list owner, PO Received or Material can change this.</p>
      )}
    </div>
  );
}

/* Value-is-the-click-target inline edit (the stage-row affordance pattern).
 * ZERO layout shift: the cell's text is always rendered (it alone sets the
 * size); the pencil floats in a permanently reserved gutter; while editing,
 * the text goes invisible and the input is laid exactly over it with the same
 * font, padding and line-height. The focus ring is an outline, so no border
 * ever adds a pixel. Enter saves, Esc cancels. */
function EditableText({ value, onSave, disabled, placeholder = "", mono, numeric, right, required, lockedTitle, multiline, gutter }) {
  const [editing, setEditing] = useState(false);
  const [v, setV] = useState(value || "");
  const ref = useRef(null);
  useEffect(() => { if (!editing) setV(value || ""); }, [value, editing]);
  useEffect(() => { if (editing) ref.current?.select(); }, [editing]);
  const commit = async () => {
    const t = v.trim();
    if (t === (value || "").trim()) { setEditing(false); return; }
    if (required && !t) { toast.error("This can't be empty"); return; }
    if (numeric && (!/^\d+(\.\d+)?$/.test(t) || Number(t) <= 0)) { toast.error("Enter a number above 0"); return; }
    const ok = await onSave(t);
    if (ok) setEditing(false);
  };
  const type = mono ? "font-mono-data font-semibold text-[12.5px] leading-[1.45]" : "text-[13px] leading-[1.45]";
  // "wide" reserves room for the pencil AND the floating + note button.
  const box = `block w-full rounded-md px-1.5 py-1 ${right ? "text-right pl-5 pr-1.5" : gutter === "wide" ? "text-left pr-12" : "text-left pr-5"} ${type}`;
  // While editing, the invisible text mirrors what's typed, so the cell only
  // grows if the content itself grows — never because of the editor.
  const shown = (editing ? v : value) || <span className="text-[hsl(var(--muted-foreground))]">{placeholder || "\u00a0"}</span>;
  if (disabled) {
    return <div className="-mx-1.5 -my-1"><span className={`${box} break-words`} title={lockedTitle}>{shown}</span></div>;
  }
  const Field = multiline ? "textarea" : "input";
  return (
    <div className="relative -mx-1.5 -my-1 group/e">
      <button type="button" onClick={() => setEditing(true)} tabIndex={editing ? -1 : 0}
        className={`${box} break-words hover:bg-[hsl(var(--muted))]/70 ${editing ? "invisible" : ""}`}>
        {shown}
      </button>
      {!editing && (
        <Pencil aria-hidden="true"
          className={`pointer-events-none absolute top-[7px] ${right ? "left-1.5" : gutter === "wide" ? "right-8" : "right-1.5"} w-3 h-3 opacity-0 group-hover/e:opacity-50 transition-opacity`} />
      )}
      {editing && (
        <Field ref={ref} value={v} onChange={e => setV(multiline ? e.target.value.replace(/\n/g, " ") : e.target.value)} onBlur={commit}
          onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); commit(); } if (e.key === "Escape") setEditing(false); }}
          inputMode={numeric ? "decimal" : undefined} rows={multiline ? 1 : undefined}
          className={`absolute inset-0 w-full h-full m-0 border-0 bg-[hsl(var(--card))] resize-none overflow-hidden outline-none ring-2 ring-orange-500/50 ${box}`} />
      )}
    </div>
  );
}

function NoteInput({ onSave, onCancel }) {
  const [t, setT] = useState("");
  return (
    <div className="flex gap-1.5 mt-2">
      <input autoFocus value={t} onChange={e => setT(e.target.value)} placeholder="Note for everyone on this part…" aria-label="Note"
        onKeyDown={e => { if (e.key === "Enter" && t.trim()) onSave(t.trim()); if (e.key === "Escape") onCancel(); }}
        className={inputCls} maxLength={500} />
      <button type="button" className={btnPrimary} disabled={!t.trim()} onClick={() => onSave(t.trim())}>Add</button>
    </div>
  );
}

function NewPartRow({ cols, onAdd, onCancel }) {
  const [no, setNo] = useState(""); const [desc, setDesc] = useState(""); const [qty, setQty] = useState("1");
  const [err, setErr] = useState("");
  const noRef = useRef(null);
  const add = async () => {
    if (!desc.trim()) { setErr("Description is required"); return; }
    if (!/^\d+(\.\d+)?$/.test(qty.trim()) || Number(qty) <= 0) { setErr("Qty must be a number above 0"); return; }
    const ok = await onAdd({ part_no: no.trim(), description: desc.trim(), qty: Number(qty) });
    if (ok) { setNo(""); setDesc(""); setQty("1"); setErr(""); noRef.current?.focus(); }
  };
  const key = (e) => { if (e.key === "Enter") add(); if (e.key === "Escape") onCancel(); };
  return (
    <tr className="bg-orange-500/[0.04]">
      <td className="px-3.5 py-2.5 border-t border-[hsl(var(--border))]/60"><input ref={noRef} autoFocus value={no} onChange={e => setNo(e.target.value)} onKeyDown={key} placeholder="Part no" aria-label="Part no" className={`${inputCls} font-mono-data`} /></td>
      <td className="px-3.5 py-2.5 border-t border-[hsl(var(--border))]/60"><input value={desc} onChange={e => { setDesc(e.target.value); setErr(""); }} onKeyDown={key} placeholder="Description (required)" aria-label="Description" className={`${inputCls} ${err && !desc.trim() ? "border-red-500" : ""}`} /></td>
      <td className="px-3.5 py-2.5 border-t border-[hsl(var(--border))]/60"><input value={qty} onChange={e => setQty(e.target.value)} onKeyDown={key} aria-label="Qty" inputMode="decimal" className={`${inputCls} font-mono-data text-right`} /></td>
      <td colSpan={cols - 3} className="px-3.5 py-2.5 border-t border-[hsl(var(--border))]/60">
        <div className="flex items-center gap-2 flex-wrap">
          <button type="button" className={btnPrimary} onClick={add}>Add part</button>
          <button type="button" className={btnGhost} onClick={onCancel}>Cancel</button>
          <span className={`text-[11px] ${err ? "text-red-600 dark:text-red-400 font-semibold" : "text-[hsl(var(--muted-foreground))]"}`}>
            {err || "Enter adds it and opens the next blank row · Esc cancels"}
          </span>
        </div>
      </td>
    </tr>
  );
}


